/**
 * DENET — proje listesi
 *
 * Sepet tek seferlik alışveriş içindir. Proje bazlı çalışan tesisatçı
 * günlerce malzeme toplar, sonunda ya sepete atar ya teklif ister.
 * Bu yüzden liste sepetten ayrı ve kalıcı: localStorage'da, birden fazla
 * projeye adlandırılabilir şekilde durur.
 *
 * Kalemler yalnızca handle + varyant + adet olarak saklanır; başlık, fiyat
 * ve görsel liste açılınca tazelenir. Fiyat değişirse eski fiyatı göstermek
 * teklif hazırlayan kişiyi yanıltır.
 */

import { DenetElement, define, qsa, emit, EVENTS,
         trapFocus, lockScroll, unlockScroll } from '@denet/core';

const KEY = 'denet:projects';

function read() {
  try {
    const d = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (!Array.isArray(d.projects)) return { projects: [], active: null };
    return d;
  } catch {
    return { projects: [], active: null };
  }
}

function write(d) {
  try { localStorage.setItem(KEY, JSON.stringify(d)); } catch { /* gizli sekme */ }
  document.dispatchEvent(new CustomEvent('denet:project:change', { detail: d }));
}

function ensureActive(d, defaultName) {
  if (!d.projects.length) {
    d.projects.push({ id: String(Date.now()), name: defaultName, items: [] });
  }
  if (!d.active || !d.projects.some((p) => p.id === d.active)) {
    d.active = d.projects[0].id;
  }
  return d;
}

const total = (d) => d.projects.reduce((n, p) => n + p.items.length, 0);

class DnProject extends DenetElement {
  mounted() {
    this.dialog = this.querySelector('dialog');
    this.body = this.ref('body');
    this.countEl = this.ref('count');
    this.defaultName = this.dataset.defaultName || 'Proje 1';

    /* Kartlar sonradan da gelebiliyor (ray, muadil, son baktıkların):
       tek dinleyici belgeye bağlanır. */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest?.('[data-project-add]');
      if (!btn) return;
      e.preventDefault();
      this.#add(btn.dataset.projectAdd, btn.dataset.variant, btn);
    }, { signal: this.signal });

    this.ref('open')?.addEventListener('click', () => this.#open(), { signal: this.signal });
    this.ref('close')?.addEventListener('click', () => this.dialog?.close(), { signal: this.signal });

    document.addEventListener('denet:project:change', () => this.#badge(), { signal: this.signal });
    this.#badge();
  }

  #badge() {
    const n = total(read());
    if (this.countEl) {
      this.countEl.textContent = String(n);
      this.countEl.hidden = n === 0;
    }
    this.classList.toggle('is-empty', n === 0);

    /* Mobil alt çubuktaki sekme de aynı sayıyı gösteriyor. Kendi dinleyicisini
       kurmak yerine buradan besliyoruz: sayaç tek yerden hesaplansın. */
    for (const el of document.querySelectorAll('[data-project-count]')) {
      el.textContent = String(n);
      el.hidden = n === 0;
    }
  }

  /**
   * @param {string} handle
   * @param {string} variantId
   * @param {HTMLElement} btn
   */
  #add(handle, variantId, btn) {
    const d = ensureActive(read(), this.defaultName);
    const project = d.projects.find((p) => p.id === d.active);
    const found = project.items.find((i) => i.handle === handle);

    if (found) found.qty += 1;
    else project.items.push({ handle, variantId, qty: 1 });

    write(d);

    /* Düğme kısa süre onay gösterir: liste kapalıyken de geri bildirim şart. */
    const label = btn.getAttribute('aria-label');
    btn.classList.add('is-added');
    btn.setAttribute('aria-label', this.dataset.added || 'Projeye eklendi');
    window.setTimeout(() => {
      btn.classList.remove('is-added');
      if (label) btn.setAttribute('aria-label', label);
    }, 1400);

    emit('denet:toast', { message: `${project.name}: ${this.dataset.added || 'eklendi'}`, tone: 'ok' });
  }

  async #open() {
    if (!this.dialog || !this.body) return;
    this.body.innerHTML = `<p class="dn-project__loading">${this.dataset.loading || 'Yükleniyor'}…</p>`;
    this.dialog.showModal();
    lockScroll();
    this.release = trapFocus(this.dialog);
    this.dialog.addEventListener('close', () => {
      unlockScroll(); this.release?.();
    }, { once: true });
    await this.#render();
  }

  async #render() {
    const d = ensureActive(read(), this.defaultName);
    const project = d.projects.find((p) => p.id === d.active);
    const root = window.Shopify?.routes?.root || '/';

    /* Fiyat ve başlık her açılışta tazelenir. */
    const rows = await Promise.all(project.items.map(async (item) => {
      try {
        const res = await fetch(`${root}products/${item.handle}?view=compare`);
        if (!res.ok) return null;
        const p = await res.json();
        return { ...item, ...p };
      } catch { return null; }
    }));

    const live = rows.filter(Boolean);
    const esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));

    const tabs = d.projects.map((p) => `
      <button class="dn-project__tab${p.id === d.active ? ' is-active' : ''}" type="button" data-pick="${p.id}">
        ${esc(p.name)} <span>${p.items.length}</span>
      </button>`).join('');

    const items = live.length ? live.map((r) => `
      <li class="dn-project__row" data-handle="${esc(r.handle)}">
        ${r.image ? `<img src="${esc(r.image)}" alt="" width="56" height="56" loading="lazy">` : '<span class="dn-project__noimg"></span>'}
        <span class="dn-project__info">
          ${r.vendor ? `<span class="dn-project__vendor">${esc(r.vendor)}</span>` : ''}
          <a href="${esc(r.url)}">${esc(r.title)}</a>
          ${r.sku ? `<span class="dn-project__sku">${esc(r.sku)}</span>` : ''}
        </span>
        <span class="dn-project__qty">
          <button type="button" data-step="-1" aria-label="Azalt">−</button>
          <input type="number" min="1" value="${r.qty}" data-qty>
          <button type="button" data-step="1" aria-label="Artır">+</button>
        </span>
        <span class="dn-project__price">${esc(r.price)}</span>
        <button class="dn-project__remove" type="button" data-remove aria-label="Kaldır">×</button>
      </li>`).join('')
      : `<li class="dn-project__empty">${this.dataset.empty || 'Bu proje boş.'}</li>`;

    this.body.innerHTML = `
      <div class="dn-project__tabs">${tabs}
        <button class="dn-project__tab dn-project__tab--new" type="button" data-new>+ ${this.dataset.newLabel || 'Yeni proje'}</button>
      </div>
      <ul class="dn-project__list">${items}</ul>
      <div class="dn-project__foot">
        <button class="dn-btn dn-btn--secondary dn-btn--sm" type="button" data-rename>${this.dataset.rename || 'Adını değiştir'}</button>
        <button class="dn-btn dn-btn--secondary dn-btn--sm" type="button" data-mail>${this.dataset.quote || 'Teklif iste'}</button>
        <button class="dn-btn dn-btn--primary" type="button" data-tocart ${live.length ? '' : 'disabled'}>
          ${this.dataset.tocart || 'Hepsini sepete ekle'}
        </button>
      </div>`;

    this.#bind(d, project, live);
  }

  /**
   * @param {any} d
   * @param {any} project
   * @param {any[]} live
   */
  #bind(d, project, live) {
    const save = () => { write(d); this.#render(); };

    qsa('[data-pick]', this.body).forEach((b) => b.addEventListener('click', () => {
      d.active = b.dataset.pick; save();
    }));

    this.body.querySelector('[data-new]')?.addEventListener('click', () => {
      const name = window.prompt(this.dataset.newPrompt || 'Proje adı', `Proje ${d.projects.length + 1}`);
      if (!name) return;
      const p = { id: String(Date.now()), name, items: [] };
      d.projects.push(p); d.active = p.id; save();
    });

    this.body.querySelector('[data-rename]')?.addEventListener('click', () => {
      const name = window.prompt(this.dataset.newPrompt || 'Proje adı', project.name);
      if (name) { project.name = name; save(); }
    });

    qsa('.dn-project__row', this.body).forEach((row) => {
      const handle = row.dataset.handle;
      const item = project.items.find((i) => i.handle === handle);
      if (!item) return;

      qsa('[data-step]', row).forEach((b) => b.addEventListener('click', () => {
        item.qty = Math.max(1, item.qty + Number(b.dataset.step));
        save();
      }));
      row.querySelector('[data-qty]')?.addEventListener('change', (e) => {
        item.qty = Math.max(1, Number(e.target.value) || 1); save();
      });
      row.querySelector('[data-remove]')?.addEventListener('click', () => {
        project.items = project.items.filter((i) => i.handle !== handle); save();
      });
    });

    this.body.querySelector('[data-tocart]')?.addEventListener('click', async () => {
      const root = window.Shopify?.routes?.root || '/';
      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: live.map((r) => ({ id: r.variantId, quantity: r.qty })),
          }),
        });
        if (!res.ok) throw new Error();
        this.dialog?.close();
        emit(EVENTS.cartUpdated);
        emit(EVENTS.drawerOpen, { source: 'project' });
      } catch {
        emit(EVENTS.cartError);
      }
    });

    this.body.querySelector('[data-mail]')?.addEventListener('click', () => {
      const lines = live.map((r) => `${r.qty} x ${r.sku || ''} ${r.title} — ${r.price}`).join('\n');
      const subject = `${this.dataset.quoteSubject || 'Teklif talebi'}: ${project.name}`;
      const body = `${this.dataset.quoteIntro || 'Merhaba, aşağıdaki liste için teklif istiyorum:'}\n\n${lines}\n`;
      window.location.href =
        `mailto:${this.dataset.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    });
  }
}

define('dn-project', DnProject);
